import {
  AccessToken,
  AuthState,
  IDToken,
  OktaAuth,
  OktaAuthOptions,
} from '@okta/okta-auth-js';

import Constants from 'constants/index';

import {
  AccessTokenClaims,
  IdTokenClaims,
  parseAccessTokenClaims,
  parseIdUserClaims,
} from './okta-utils';

const transformAuthState: OktaAuthOptions['transformAuthState'] = async (
  _,
  authState,
) => {
  if (Constants.IS_IN_CYPRESS_TEST_LOCAL) {
    authState.isAuthenticated = true;

    authState.idToken = 'dev' as unknown as IDToken;
    authState.accessToken = 'dev' as unknown as AccessToken;
    return authState;
  }
  // below from: https://github.com/okta/okta-auth-js
  // extra requirement: user must have valid Okta SSO session
  // const user = await oktaAuth.token.getUserInfo();
  // authState.isAuthenticated = true; // convert to boolean
  // authState.users = user; // also store user object on authState
  return authState;
};

const oktaConfig: OktaAuthOptions = {
  issuer: Constants.OKTA_ISSUER || 'http://bogus.jest',
  clientId: Constants.OKTA_CLIENT_ID || 'jest',
  redirectUri: window.location.origin + '/login/callback',
  pkce: true,
  scopes: [
    'openid',
    'email',
    'profile',
    'location_info',
    'job_info',
    'offline_access',
  ],
  transformAuthState,
  tokenManager: { autoRenew: false },
};
const oktaAuth = new OktaAuth(oktaConfig);

export default oktaAuth;

export interface CfaAuthState {
  authState: AuthState;
  accessTokenClaims?: AccessTokenClaims;
  idTokenClaims?: IdTokenClaims;
}

const authSubscribers = new Set<(authState: AuthState) => void>();

export const clearAuthSubscribers = () => {
  authSubscribers.forEach(fn => {
    oktaAuth.authStateManager.unsubscribe(fn);
  });
  authSubscribers.clear();
};

/** Subscribe to changes in the Okta auth state */
export const subscribeToAuthStateChange = (
  fn: (state: CfaAuthState) => void | Promise<void>,
): {
  unsubscribe: () => void;
} => {
  const subscriptionHandler = (authState: AuthState) => {
    const accessTokenClaims = authState.accessToken?.claims
      ? parseAccessTokenClaims(authState.accessToken.claims)
      : undefined;
    const idTokenClaims = authState.idToken?.claims
      ? parseIdUserClaims(authState.idToken.claims)
      : undefined;

    fn({ authState, accessTokenClaims, idTokenClaims });
  };

  oktaAuth.authStateManager.subscribe(subscriptionHandler);
  authSubscribers.add(subscriptionHandler);

  return {
    unsubscribe: () => {
      oktaAuth.authStateManager.unsubscribe(subscriptionHandler);
      authSubscribers.delete(subscriptionHandler);
    },
  };
};
